import React from "react";
import { Box, Typography, LinearProgress, Button, Tooltip } from "@mui/material";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import ReplayIcon from "@mui/icons-material/Replay";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { BORDER, BRAND, BRAND_SOFT, CORRECTION_STATUSES, CORRECTION_STATUS_ORDER, SEVERITIES, TEXT_PRIMARY, TEXT_SECONDARY } from "../constants";

const Label = ({ children }) => (
  <Typography
    variant="caption"
    fontWeight={700}
    color={TEXT_SECONDARY}
    sx={{ width: { sm: 120 }, flexShrink: 0, pt: { sm: "3px" } }}
  >
    {children}
  </Typography>
);

const Row = ({ label, children }) => (
  <Box sx={{ display: "flex", flexDirection: { xs: "column", sm: "row" }, gap: { xs: 0.75, sm: 1.5 }, alignItems: { sm: "flex-start" } }}>
    <Label>{label}</Label>
    <Box sx={{ flex: 1, minWidth: 0 }}>{children}</Box>
  </Box>
);

const SegmentedBar = ({ summary }) => (
  <Box sx={{ display: "flex", gap: "3px", height: 8, width: { xs: "100%", sm: 260 }, borderRadius: 999, overflow: "hidden", bgcolor: "#eef2f6" }}>
    {["done", "partially_done", "needs_review", "not_done"].map((status) =>
      summary[status] > 0 ? (
        <Tooltip key={status} title={`${summary[status]} ${CORRECTION_STATUSES[status].label}`}>
          <Box sx={{ flex: summary[status], bgcolor: CORRECTION_STATUSES[status].bar, transition: "flex .6s ease" }} />
        </Tooltip>
      ) : null,
    )}
  </Box>
);

const StatusChipButton = ({ status, count, onClick }) => {
  const config = CORRECTION_STATUSES[status];
  return (
    <Box
      component="button"
      type="button"
      onClick={onClick}
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.6,
        px: 1,
        py: 0.25,
        border: "none",
        borderRadius: 999,
        bgcolor: config.bg,
        color: config.color,
        font: "inherit",
        fontSize: 13,
        fontWeight: 700,
        cursor: "pointer",
        "&:hover, &:focus-visible": { boxShadow: `0 0 0 2px ${config.bar}55`, outline: "none" },
      }}
    >
      <Box sx={{ width: 7, height: 7, borderRadius: "50%", bgcolor: config.bar }} />
      {count} {config.label}
    </Box>
  );
};

const CorrectionsRow = ({ check, onStatusClick, onRerun, rerunning }) => {
  const control = check.correctionsControl;
  const count = check.corrections?.length || 0;
  const actionSx = { borderRadius: 2, fontWeight: 600, py: 0.25 };

  if (check.status === "controlling") {
    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
        <LinearProgress sx={{ width: { xs: "100%", sm: 260 }, height: 8, borderRadius: 999, bgcolor: BRAND_SOFT }} />
        <Typography variant="body2" color={TEXT_PRIMARY} fontWeight={600} sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <AutoAwesomeIcon sx={{ fontSize: 16, color: BRAND }} />
          בודק את יישום {count} התיקונים… המסך יתעדכן אוטומטית
        </Typography>
      </Box>
    );
  }

  if (check.status === "failed" || control?.state === "failed") {
    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
        <ErrorOutlineIcon sx={{ color: "#b91c1c", fontSize: 18 }} />
        <Typography variant="body2" color="#991b1b" fontWeight={600}>
          הבקרה לא הושלמה: {check.errorMessage || control?.error || "אירעה שגיאה"}
        </Typography>
        <Button size="small" variant="outlined" startIcon={<ReplayIcon />} onClick={onRerun} disabled={rerunning} sx={actionSx}>
          הרצה מחדש
        </Button>
      </Box>
    );
  }

  if (!control && count > 0) {
    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
        <Typography variant="body2" color={TEXT_SECONDARY}>
          הבדיקה נפתחה לפני שנוספה בקרת התיקונים.
        </Typography>
        <Button size="small" variant="outlined" startIcon={<AutoAwesomeIcon />} onClick={onRerun} disabled={rerunning} sx={actionSx}>
          הרצת בקרת תיקונים
        </Button>
      </Box>
    );
  }

  if (!control || control.state === "skipped") return null;

  const { summary } = control;
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
      <SegmentedBar summary={summary} />
      <Typography variant="body2" fontWeight={700} color={TEXT_PRIMARY}>
        {summary.done}/{summary.total} בוצעו במלואם
      </Typography>
      <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
        {CORRECTION_STATUS_ORDER.filter((status) => summary[status] > 0).map((status) => (
          <StatusChipButton key={status} status={status} count={summary[status]} onClick={() => onStatusClick(status)} />
        ))}
      </Box>
    </Box>
  );
};

const Stat = ({ value, label, color, marker }) => (
  <Box sx={{ px: { xs: 1.5, sm: 2.25 }, py: 1.75, minWidth: 0 }}>
    <Typography sx={{ fontSize: 24, fontWeight: 700, lineHeight: 1.1, color: TEXT_PRIMARY }}>{value || 0}</Typography>
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mt: 0.5 }}>
      {marker && <Box sx={{ width: 8, height: 8, borderRadius: "2px", bgcolor: color, flexShrink: 0 }} />}
      <Typography sx={{ fontSize: 13, color: TEXT_SECONDARY, whiteSpace: "nowrap" }}>{label}</Typography>
    </Box>
  </Box>
);

// The key numbers of the check in one flat strip (spec 3.6 counts)
const FindingsStrip = ({ check }) => {
  const summary = check.comparison?.summary;
  const autoChecks = check.autoChecks?.summary;
  if (!summary) return null;
  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: { xs: "repeat(3, 1fr)", sm: "repeat(5, 1fr)" },
        bgcolor: "#fff",
        border: `1px solid ${BORDER}`,
        borderRadius: 2,
        "& > *:not(:last-of-type)": { borderInlineEnd: { sm: `1px solid ${BORDER}` } },
      }}
    >
      <Stat value={summary.total} label="הבדלים בין הגרסאות" />
      {SEVERITIES.map(({ key, label, color }) => (
        <Stat key={key} value={summary[key]} label={label} color={color} marker />
      ))}
      {autoChecks && <Stat value={autoChecks.total} label="ממצאי בדיקה אוטומטית" color="#b91c1c" marker />}
    </Box>
  );
};

/**
 * Summary inside the results header: finding counts by severity first,
 * then the corrections status (spec 3.6). The details live in the tabs.
 */
const ResultSummary = ({ check, onStatusClick, onRerun, rerunning }) => (
  <Box sx={{ display: "flex", flexDirection: "column", gap: 1.75 }}>
    <FindingsStrip check={check} />
    {/* Corrections status only when a corrections list was entered */}
    {check.corrections?.length > 0 && (
      <Row label="בקרת תיקונים">
        <CorrectionsRow check={check} onStatusClick={onStatusClick} onRerun={onRerun} rerunning={rerunning} />
      </Row>
    )}
  </Box>
);

export default ResultSummary;
